import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getSection } from '@/config/navigation';
import { PageSection } from '@/components/ui/page-section';
import { useSettings } from '@/services/SettingsContext';
import { createCapsifiService, ValueStream as CapsifiValueStream } from '@/services/CapsifiService';
import { transformValueStreamData } from './valueStreamService';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Settings2, ZoomIn, ZoomOut, RotateCcw, Maximize2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { BarChart3, LineChart } from 'lucide-react';

export function BusinessModelSection() {
  const section = getSection('model');
  const { apiSettings, isConfigured, setShowModelSelector } = useSettings();
  const [valueStreamData, setValueStreamData] = useState<ReturnType<typeof transformValueStreamData> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [valueStreams, setValueStreams] = useState<CapsifiValueStream[]>([]);
  const [selectedValueStreamId, setSelectedValueStreamId] = useState<string | null>(null);
  const [showMetrics, setShowMetrics] = useState(true);
  const [showOnlyWithData, setShowOnlyWithData] = useState(false);

  // Check if we should fetch data
  const shouldFetchData = 
    Boolean(apiSettings.token) && 
    Boolean(apiSettings.selectedModelId) && 
    Boolean(isConfigured);

  // Always initialize the service, but we'll control when it makes API calls
  const service = useMemo(() => 
    createCapsifiService(apiSettings.selectedModelId || ''), 
    [apiSettings.selectedModelId]
  );

  // Fetch value streams list
  const fetchValueStreams = useCallback(async () => {
    if (!shouldFetchData) {
      console.debug('Missing required configuration - cannot fetch value streams');
      return;
    }

    try {
      const data = await service.getValueStreams();
      setValueStreams(data);
      
      // If we have value streams and none is selected, select the first one
      if (data.length > 0 && !selectedValueStreamId) {
        setSelectedValueStreamId(data[0].id);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch value streams list';
      console.error('Error fetching value streams list:', errorMessage);
      
      if (errorMessage.includes('Unauthorized')) {
        setShowModelSelector(true);
      }
    }
  }, [service, shouldFetchData, selectedValueStreamId, setShowModelSelector]);

  // Fetch value stream details
  const fetchValueStreamDetails = useCallback(async () => {
    if (!shouldFetchData || !selectedValueStreamId) {
      console.debug('Missing required configuration or no value stream selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.debug(`Fetching value stream details for ID: ${selectedValueStreamId}`);
      const data = await service.getValueStreamDetails(selectedValueStreamId);
      const transformedData = transformValueStreamData(data);
      setValueStreamData(transformedData);
      setRetryCount(0);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch value stream details';
      console.error('Error fetching value stream details:', errorMessage);
      setError(errorMessage);
      
      if (errorMessage.includes('No credentials provided')) {
        return;
      }
      
      if (errorMessage.includes('Unauthorized')) {
        setShowModelSelector(true);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedValueStreamId, service, shouldFetchData, setShowModelSelector]);

  // Manual retry function
  const refetch = useCallback(() => {
    setRetryCount(0);
    fetchValueStreamDetails();
  }, [fetchValueStreamDetails]);

  // Fetch value streams list when dependencies change
  useEffect(() => {
    if (shouldFetchData) {
      console.debug('Token and model ID available - initiating value streams fetch');
      fetchValueStreams();
    } else {
      console.debug('Missing required configuration - skipping value streams fetch');
    }
  }, [shouldFetchData, fetchValueStreams]);

  // Fetch value stream details when selected ID changes
  useEffect(() => {
    if (selectedValueStreamId) {
      fetchValueStreamDetails();
    }
  }, [selectedValueStreamId, fetchValueStreamDetails]);

  const handleSelect = (item: { data: any; type: string }) => {
    console.debug('Selected item:', item);
  };

  const handleValueStreamChange = (value: string) => {
    setSelectedValueStreamId(value);
    setValueStreamData(null); // Clear current data while loading new selection
  };

  // Show configuration prompt if not configured
  if (!isConfigured || !apiSettings.selectedModelId || !apiSettings.token) {
    return (
      <PageSection section={section}>
        <div className="flex flex-col items-center justify-center p-8 text-center h-full">
          <p className="text-muted-foreground mb-4">
            Please configure your settings to get started.
          </p>
          <Button onClick={() => setShowModelSelector(true)}>
            Configure Settings
          </Button>
        </div>
      </PageSection>
    );
  }

  // Don't try to render with the hook result until we're sure we should fetch data
  if (!shouldFetchData) {
    return (
      <PageSection section={section}>
        <div className="flex flex-col items-center justify-center p-8 text-center h-full">
          <p className="text-muted-foreground mb-4">
            Loading configuration...
          </p>
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageSection>
    );
  }

  return (
    <PageSection section={section}>
      <div className="flex flex-col h-full">
        {/* Value Stream Dropdown */}
        <div className="px-4 pb-4">
          <Select
            value={selectedValueStreamId || undefined}
            onValueChange={handleValueStreamChange}
          >
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select a value stream" />
            </SelectTrigger>
            <SelectContent>
              {valueStreams.map((vs) => (
                <SelectItem key={vs.id} value={vs.id}>
                  {vs.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-grow min-h-0 overflow-hidden relative border rounded-lg">
          {/* Metric Controls - Only show when value stream is selected */}
          {selectedValueStreamId && (
            <div className="absolute top-0 left-0 right-0 z-10 px-4 py-3 bg-background/95 backdrop-blur-sm border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="show-metrics" 
                      checked={showMetrics} 
                      onCheckedChange={setShowMetrics}
                    />
                    <Label htmlFor="show-metrics" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                      <BarChart3 className="h-4 w-4" />
                      Show Metrics
                    </Label>
                  </div>
                  
                  {showMetrics && (
                    <div className="flex items-center gap-2">
                      <Switch 
                        id="show-with-data" 
                        checked={showOnlyWithData} 
                        onCheckedChange={setShowOnlyWithData}
                      />
                      <Label htmlFor="show-with-data" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                        <LineChart className="h-4 w-4 text-[#8884d8]" />
                        Show only metrics with data
                      </Label>
                    </div>
                  )}
                </div>

                {/* Zoom controls */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // These will be handled by ValueStreamMap
                      window.dispatchEvent(new CustomEvent('value-stream-zoom-in'));
                    }}
                    title="Zoom In"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('value-stream-zoom-out'));
                    }}
                    title="Zoom Out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('value-stream-reset'));
                    }}
                    title="Reset"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('value-stream-fit'));
                    }}
                    title="Fit to View"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-20">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
              <Alert variant="destructive" className="max-w-md">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex space-x-4 mt-4">
                <Button
                  variant="outline"
                  onClick={() => refetch()}
                >
                  Retry
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowModelSelector(true)}
                  title="Open Settings"
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {!loading && !error && !valueStreamData && (
            <div className="flex items-center justify-center h-full">
              <div className="text-muted-foreground">
                {selectedValueStreamId ? 'Loading value stream data...' : 'Please select a value stream'}
              </div>
            </div>
          )}
        </div>
      </div>
    </PageSection>
  );
} 